import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { DeleteModal } from '@common/components';
import TableWithPagination from '@common/components/TableWithPagination';
import Toolbar from '@common/components/Toolbar';
import { createEmptyFilter } from '@common/utils/filterUtils';
import useAsyncFilterOptions from '@common/hooks/useAsyncFilterOptions';
import {
  searchSavedReports,
  bulkDeleteSavedReports,
  updateSavedReport,
  getSavedReportFilterOptions,
  convertSavedReportFiltersToApiFormat,
} from '@api/savedReports';
import { fetchDashboardById } from '@api/dashboard';
import { createColumns } from './tableColumns';
import SelectedSavedReportsBar from './SelectedSavedReportsBar/SelectedSavedReportsBar';
import SaveReportModal from '../SaveReportModal/SaveReportModal';
import * as styles from './styles.module.css';

// Status constants
const STATUS_ENABLED = 1;
const STATUS_DISABLED = 0;

/**
 * Filter types for saved reports
 * isAsync: true - options loaded from API, false - static options
 */
const SAVED_REPORTS_FILTER_TYPES = [
  { value: 'name', label: 'Name', isAsync: true },
  { value: 'description', label: 'Description', isAsync: true },
  { value: 'dashboardName', label: 'Report Template', isAsync: true },
  { value: 'status', label: 'Status', isAsync: false },
];

/**
 * Get translated label for a filter type
 * @param {string} filterValue - The filter type value
 * @param {function} t - Translation function
 * @returns {string} - Translated label
 */
const getTranslatedFilterLabel = (filterValue, t) => {
  switch (filterValue) {
    case 'name':
      return t('common.name');
    case 'description':
      return t('common.description');
    case 'dashboardName':
      return t('savedReports.filters.reportTemplate');
    case 'status':
      return t('savedReports.filters.status');
    default:
      return filterValue;
  }
};

/**
 * Get filter types with translated labels
 */
const getFilterTypes = (t) =>
  SAVED_REPORTS_FILTER_TYPES.map((type) => ({
    ...type,
    label: getTranslatedFilterLabel(type.value, t),
  }));

/**
 * Get filter values with translated labels
 */
const getFilterValues = (t) => ({
  status: [
    { value: '1', label: t('savedReports.status.enabled') },
    { value: '0', label: t('savedReports.status.disabled') },
  ],
});

/**
 * Check if a filter type is async (loads options from API)
 */
const isAsyncFilterType = (filterType) => {
  const type = SAVED_REPORTS_FILTER_TYPES.find((t) => t.value === filterType);
  return type?.isAsync ?? false;
};

/**
 * Build filters for API request
 */
const buildSavedReportFilters = (filters) => {
  const apiFilters = [];

  filters.forEach((filter) => {
    if (filter.type && filter.value) {
      // For multi-select filters, use the first value
      const value = Array.isArray(filter.value)
        ? filter.value[0]
        : filter.value;
      if (value) {
        apiFilters.push({
          type: filter.type,
          value,
        });
      }
    }
  });

  return apiFilters;
};

/**
 * SavedReportsTableView Component
 *
 * Displays a table of saved reports with search, filtering, and actions
 * @param {boolean} isAdmin - Whether the current user has admin privileges
 * @param {number} userContactId - The current user's contact ID for ownership checks
 */
export default function SavedReportsTableView({ isAdmin, userContactId }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [selectedRowIds, setSelectedRowIds] = useState({});
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sorting, setSorting] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [columnSizing, setColumnSizing] = useState({});
  const [columnSizingInfo, setColumnSizingInfo] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState([createEmptyFilter()]);
  const [selectAllMode, setSelectAllMode] = useState(false);
  const [deselectedRowIds, setDeselectedRowIds] = useState({});

  // Edit name/description modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingSavedReport, setEditingSavedReport] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState(null);

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteStatus, setDeleteStatus] = useState('idle');
  const [deleteResults, setDeleteResults] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [openAccordion, setOpenAccordion] = useState(null);
  const [selectedSavedReport, setSelectedSavedReport] = useState(null);

  const getRowId = useCallback((row) => String(row.id), []);

  const refreshData = useCallback(() => {
    setRefreshTrigger((prev) => prev + 1);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const apiFilters = buildSavedReportFilters(filters);

        const result = await searchSavedReports({
          page: pageIndex,
          pageSize,
          sortField: sorting[0]?.id || 'createdAt',
          sortOrder: sorting[0]?.desc ? 'desc' : 'asc',
          filters: apiFilters,
          searchTerm,
        });

        if (result?.rows) {
          setData(result.rows);
          setTotalCount(result.total || 0);
        } else {
          setData([]);
          setTotalCount(0);
        }
      } catch (error) {
        console.error('Error fetching saved reports:', error);
        setData([]);
        setTotalCount(0);
      }
    };
    fetchData();
  }, [pageIndex, pageSize, sorting, searchTerm, filters, refreshTrigger]);

  useEffect(() => {
    setPageIndex(1);
  }, [sorting, searchTerm, filters]);

  // Handle viewing a saved report (load dashboard with saved configuration)
  const handleView = useCallback(
    async (savedReport) => {
      try {
        const result = await fetchDashboardById(savedReport.dashboardId);
        if (result.status === 'success' && result.data) {
          navigate(`/${savedReport.dashboardId}/view`, {
            state: {
              filters: savedReport.configuration || {},
              savedReportId: savedReport.id,
              savedReportName: savedReport.name,
              savedReportDescription: savedReport.description,
              // Store original configuration for potential "Back" navigation
              savedReportConfiguration: savedReport.configuration || {},
            },
          });
        }
      } catch (error) {
        console.error('Error loading saved report:', error);
      }
    },
    [navigate],
  );

  // Handle editing name and description
  const handleEditNameDescription = useCallback((savedReport) => {
    setEditingSavedReport(savedReport);
    setEditError(null);
    setEditModalOpen(true);
  }, []);

  // Handle saving edited name/description
  const handleSaveNameDescription = useCallback(
    async ({ name, description }) => {
      if (!editingSavedReport) return;

      setEditSaving(true);
      setEditError(null);

      try {
        const result = await updateSavedReport(editingSavedReport.id, {
          name,
          description,
        });

        if (result.status === 'success') {
          setEditModalOpen(false);
          setEditingSavedReport(null);
          refreshData();
        } else {
          setEditError(result.message || t('savedReports.errors.updateFailed'));
        }
      } catch {
        setEditError(t('savedReports.errors.updateFailed'));
      } finally {
        setEditSaving(false);
      }
    },
    [editingSavedReport, refreshData, t],
  );

  // Handle closing the edit modal
  const handleCloseEditModal = useCallback(() => {
    setEditModalOpen(false);
    setEditingSavedReport(null);
    setEditError(null);
  }, []);

  // Handle modifying report criteria (navigate to configuration page)
  const handleModifyReportCriteria = useCallback(
    (savedReport) => {
      navigate(`/${savedReport.dashboardId}/configure`, {
        state: {
          savedReportId: savedReport.id,
          savedReportName: savedReport.name,
          savedReportDescription: savedReport.description,
          initialFilters: savedReport.configuration || {},
          isEditMode: true,
        },
      });
    },
    [navigate],
  );

  const handleEnable = useCallback(
    async (savedReport) => {
      try {
        const result = await updateSavedReport(savedReport.id, {
          status: STATUS_ENABLED,
        });
        if (result.status === 'success') {
          refreshData();
        }
      } catch (error) {
        console.error('Failed to enable saved report:', error);
      }
    },
    [refreshData],
  );

  const handleDisable = useCallback(
    async (savedReport) => {
      try {
        const result = await updateSavedReport(savedReport.id, {
          status: STATUS_DISABLED,
        });
        if (result.status === 'success') {
          refreshData();
        }
      } catch (error) {
        console.error('Failed to disable saved report:', error);
      }
    },
    [refreshData],
  );

  // Handler for inline status update via dropdown
  const handleUpdateStatus = useCallback(
    async (savedReport, newStatus) => {
      try {
        const result = await updateSavedReport(savedReport.id, { status: newStatus });
        if (result.status === 'success') {
          refreshData();
        }
      } catch (error) {
        console.error('Failed to update saved report status:', error);
      }
    },
    [refreshData],
  );

  const handleDeselectAll = useCallback(() => {
    setSelectedRowIds({});
    setSelectAllMode(false);
    setDeselectedRowIds({});
  }, []);

  const handleDeleteClick = useCallback((savedReport) => {
    setSelectedSavedReport(savedReport);
    setDeleteModalOpen(true);
    setDeleteStatus('idle');
    setDeleteResults(null);
    setDeleteError(null);
  }, []);

  const handleCloseDeleteModal = useCallback(() => {
    setDeleteModalOpen(false);
    setSelectedSavedReport(null);
    setDeleteStatus('idle');
    setDeleteResults(null);
    setDeleteError(null);
    setOpenAccordion(null);
  }, []);

  const handleDeleteSubmit = useCallback(async () => {
    setDeleteStatus('loading');
    setDeleteError(null);

    try {
      if (selectedSavedReport) {
        // Single delete
        await bulkDeleteSavedReports({ ids: [selectedSavedReport.id] });
        setDeleteResults({
          success: [{ name: selectedSavedReport.name }],
          failed: [],
        });
      } else {
        // Bulk delete
        const selectedIds = Object.keys(selectedRowIds)
          .filter((id) => selectedRowIds[id])
          .map((id) => Number(id));

        const result = await bulkDeleteSavedReports({ ids: selectedIds });

        if (result.status === 'success') {
          // Get names of deleted reports from current data
          const deletedReports = data
            .filter((report) => selectedIds.includes(report.id))
            .map((report) => ({ name: report.name }));

          setDeleteResults({
            success: deletedReports,
            failed: [],
          });
        } else {
          setDeleteError(result.message || t('savedReports.deleteConfirm.errorTitle'));
          setDeleteStatus('error');
          return;
        }
      }

      setDeleteStatus('results');
      refreshData();
      handleDeselectAll();
    } catch (error) {
      setDeleteStatus('error');
      setDeleteError(error.message || t('savedReports.deleteConfirm.errorTitle'));
    }
  }, [selectedSavedReport, selectedRowIds, data, refreshData, handleDeselectAll, t]);

  const handleBulkDelete = useCallback(() => {
    const selectedIds = Object.keys(selectedRowIds)
      .filter((id) => selectedRowIds[id])
      .map((id) => Number(id));

    if (selectedIds.length === 0) return;

    setSelectedSavedReport(null);
    setDeleteModalOpen(true);
    setDeleteStatus('idle');
    setDeleteResults(null);
    setDeleteError(null);
  }, [selectedRowIds]);

  const handleBulkEnable = useCallback(async () => {
    const selectedIds = Object.keys(selectedRowIds)
      .filter((id) => selectedRowIds[id])
      .map((id) => Number(id));

    if (selectedIds.length === 0) return;

    try {
      await Promise.all(
        selectedIds.map((id) =>
          updateSavedReport(id, { status: STATUS_ENABLED }),
        ),
      );
      setSelectedRowIds({});
      setSelectAllMode(false);
      setDeselectedRowIds({});
      refreshData();
    } catch (error) {
      console.error('Failed to enable saved reports:', error);
    }
  }, [selectedRowIds, refreshData]);

  const handleBulkDisable = useCallback(async () => {
    const selectedIds = Object.keys(selectedRowIds)
      .filter((id) => selectedRowIds[id])
      .map((id) => Number(id));

    if (selectedIds.length === 0) return;

    try {
      await Promise.all(
        selectedIds.map((id) =>
          updateSavedReport(id, { status: STATUS_DISABLED }),
        ),
      );
      setSelectedRowIds({});
      setSelectAllMode(false);
      setDeselectedRowIds({});
      refreshData();
    } catch (error) {
      console.error('Failed to disable saved reports:', error);
    }
  }, [selectedRowIds, refreshData]);

  const handleSearchChange = useCallback((newSearchTerm) => {
    setSearchTerm(newSearchTerm);
  }, []);

  const handleFiltersChange = useCallback((newFilters) => {
    setFilters(newFilters);
  }, []);

  const handleSearchRemove = useCallback(() => {
    setSearchTerm('');
  }, []);

  const handlePageSizeChange = useCallback((newPageSize) => {
    setPageSize(newPageSize);
    setPageIndex(1);
  }, []);

  const handlePreviousPage = useCallback(() => {
    setPageIndex((prev) => Math.max(1, prev - 1));
  }, []);

  const handleNextPage = useCallback(() => {
    const maxPage = Math.ceil(totalCount / pageSize);
    setPageIndex((prev) => Math.min(maxPage, prev + 1));
  }, [totalCount, pageSize]);

  const canPreviousPage = pageIndex > 1;
  const canNextPage = pageIndex < Math.ceil(totalCount / pageSize);

  const columns = useMemo(
    () =>
      createColumns(t, {
        onEditNameDescription: handleEditNameDescription,
        onModifyReportCriteria: handleModifyReportCriteria,
        onEnable: handleEnable,
        onDisable: handleDisable,
        onDelete: handleDeleteClick,
        onUpdateStatus: handleUpdateStatus,
        isAdmin,
        userContactId,
      }),
    [
      t,
      handleEditNameDescription,
      handleModifyReportCriteria,
      handleEnable,
      handleDisable,
      handleDeleteClick,
      handleUpdateStatus,
      isAdmin,
      userContactId,
    ],
  );

  const filterTypes = useMemo(() => getFilterTypes(t), [t]);
  const filterValues = useMemo(() => getFilterValues(t), [t]);

  // Use shared hook for async filter options with caching
  const { loadFilterOptions, handleFilterDropdownOpen } = useAsyncFilterOptions(
    {
      filters,
      fetchFilterOptions: getSavedReportFilterOptions,
      convertFiltersToApiFormat: convertSavedReportFiltersToApiFormat,
      isAsyncFilterType,
      staticFilterValues: filterValues,
    },
  );

  const selectedCount = useMemo(() => {
    if (selectAllMode) {
      const deselectedCount =
        Object.values(deselectedRowIds).filter(Boolean).length;
      return totalCount - deselectedCount;
    }
    return Object.values(selectedRowIds).filter(Boolean).length;
  }, [selectAllMode, totalCount, selectedRowIds, deselectedRowIds]);

  return (
    <div className={styles['saved-reports-container']}>
      <div className={styles['table-wrapper']}>
        <Toolbar
          onSearchChange={handleSearchChange}
          onFiltersChange={handleFiltersChange}
          searchTerm={searchTerm}
          onSearchRemove={handleSearchRemove}
          filters={filters}
          filterTypes={filterTypes}
          filterValues={filterValues}
          loadFilterOptions={loadFilterOptions}
          onFilterDropdownOpen={handleFilterDropdownOpen}
        />
        <TableWithPagination
          data={data}
          columns={columns}
          sorting={sorting}
          onSortingChange={setSorting}
          selectedRowIds={selectedRowIds}
          onSelectionChange={setSelectedRowIds}
          selectAllMode={selectAllMode}
          onSelectAllModeChange={setSelectAllMode}
          deselectedRowIds={deselectedRowIds}
          onDeselectedRowIdsChange={setDeselectedRowIds}
          totalCount={totalCount}
          columnSizing={columnSizing}
          onColumnSizingChange={setColumnSizing}
          columnSizingInfo={columnSizingInfo}
          onColumnSizingInfoChange={setColumnSizingInfo}
          getRowId={getRowId}
          onRowClick={handleView}
          emptyStateTranslationKey="savedReports.noSavedReports"
          pageIndex={pageIndex}
          pageSize={pageSize}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          onPageSizeChange={handlePageSizeChange}
          onPreviousPage={handlePreviousPage}
          onNextPage={handleNextPage}
        />
      </div>
      {selectedCount > 0 && (
        <SelectedSavedReportsBar
          numberOfSelected={selectedCount}
          onDeselectAll={handleDeselectAll}
          onEnable={handleBulkEnable}
          onDisable={handleBulkDisable}
          onDelete={handleBulkDelete}
        />
      )}

      {/* Edit Name & Description Modal */}
      <SaveReportModal
        isOpen={editModalOpen}
        onClose={handleCloseEditModal}
        onSave={handleSaveNameDescription}
        saving={editSaving}
        error={editError}
        defaultName={editingSavedReport?.name || ''}
        defaultDescription={editingSavedReport?.description || ''}
        isEditMode
      />

      {/* Delete Confirmation Modal */}
      <DeleteModal
        isOpen={deleteModalOpen}
        deleteStatus={deleteStatus}
        deleteResults={deleteResults}
        deleteError={deleteError}
        openAccordion={openAccordion}
        setOpenAccordion={setOpenAccordion}
        selectedCount={selectedSavedReport ? 1 : selectedCount}
        onSubmit={handleDeleteSubmit}
        onClose={handleCloseDeleteModal}
        translationPrefix="savedReports.deleteConfirm"
        getItemName={(item) => item.name}
      />
    </div>
  );
}

SavedReportsTableView.propTypes = {
  isAdmin: PropTypes.bool.isRequired,
  userContactId: PropTypes.number,
};

SavedReportsTableView.defaultProps = {
  userContactId: null,
};
